import { apiClient } from "@/shared/services/api";

interface ApiResponse<T> {
  success: boolean;
  message: string | null;
  errorCode: string | null;
  errors: Record<string, string[]> | null;
  data: T;
}

const unwrap = <T>(payload: ApiResponse<T> | T): T => {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "data" in (payload as ApiResponse<T>) &&
    "success" in (payload as ApiResponse<T>)
  ) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
};

// ── Types mirror the backend Common.DTOs.PointsConfigurationDtos ─────────────

export type PointsConfigurationStatus = "Draft" | "Submitted" | "Approved" | "Rejected";
export type PointsEarnBasis = "OrderAmount" | "ProductCategory" | "CustomerGroup";
export type PointsConditionType =
  | "CustomerGroup"
  | "ProductCategory"
  | "OrderAmount"
  | "OrderType"
  | "CampaignPeriod";
export type PointsConditionOperator = "Equals" | "GreaterThanOrEqual" | "LessThanOrEqual" | "In" | "Between";

export interface PointsConfigurationCondition {
  id: string;
  conditionType: PointsConditionType;
  operator: PointsConditionOperator;
  value: string;
}

export interface PointsConfiguration {
  id: string;
  name: string;
  status: PointsConfigurationStatus;
  earnBasis: PointsEarnBasis;
  earnRatePoints: number;
  earnRatePerAmount: number;
  pointValueKes: number;
  minRedeemablePoints: number;
  maxRedeemablePointsPerOrder?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActiveFlag: boolean;
  requestedByUserId?: string | null;
  requestedAt?: string | null;
  submittedByUserId?: string | null;
  submittedAt?: string | null;
  approvedByUserId?: string | null;
  approvedAt?: string | null;
  rejectedByUserId?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
  conditions: PointsConfigurationCondition[];
}

export interface PointsConfigurationConditionInput {
  conditionType: PointsConditionType;
  operator: PointsConditionOperator;
  value: string;
}

export interface PointsConfigurationInput {
  name: string;
  earnBasis: PointsEarnBasis;
  earnRatePoints: number;
  earnRatePerAmount: number;
  pointValueKes: number;
  minRedeemablePoints: number;
  maxRedeemablePointsPerOrder?: number | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  isActiveFlag: boolean;
  conditions: PointsConfigurationConditionInput[];
}

// The API binds enums by their integer value (matching the rest of the app, e.g.
// accountType: number). The form works with the readable names, so map them to
// the backend codes when writing. Keep in sync with FarmSmartERP.Common.Enums.
const EARN_BASIS_CODE: Record<PointsEarnBasis, number> = {
  OrderAmount: 1,
  ProductCategory: 2,
  CustomerGroup: 3,
};
const CONDITION_TYPE_CODE: Record<PointsConditionType, number> = {
  CustomerGroup: 1,
  ProductCategory: 2,
  OrderAmount: 3,
  OrderType: 4,
  CampaignPeriod: 5,
};
const CONDITION_OPERATOR_CODE: Record<PointsConditionOperator, number> = {
  Equals: 1,
  GreaterThanOrEqual: 2,
  LessThanOrEqual: 3,
  In: 4,
  Between: 5,
};

/** Convert the name-based form input into the numeric enum codes the API expects. */
const toWirePayload = (payload: PointsConfigurationInput) => ({
  ...payload,
  earnBasis: EARN_BASIS_CODE[payload.earnBasis],
  conditions: payload.conditions.map((c) => ({
    conditionType: CONDITION_TYPE_CODE[c.conditionType],
    operator: CONDITION_OPERATOR_CODE[c.operator],
    value: c.value,
  })),
});

const BASE = "/points/configurations";

export const pointsConfigApi = {
  getAll: async (status?: string): Promise<PointsConfiguration[]> => {
    const { data } = await apiClient.get<ApiResponse<PointsConfiguration[]> | PointsConfiguration[]>(
      BASE,
      { params: status ? { status } : undefined }
    );
    return unwrap<PointsConfiguration[]>(data);
  },

  getById: async (id: string): Promise<PointsConfiguration> => {
    const { data } = await apiClient.get<ApiResponse<PointsConfiguration> | PointsConfiguration>(`${BASE}/${id}`);
    return unwrap<PointsConfiguration>(data);
  },

  create: async (payload: PointsConfigurationInput): Promise<PointsConfiguration> => {
    const { data } = await apiClient.post<ApiResponse<PointsConfiguration> | PointsConfiguration>(BASE, toWirePayload(payload));
    return unwrap<PointsConfiguration>(data);
  },

  update: async (id: string, payload: PointsConfigurationInput): Promise<PointsConfiguration> => {
    const { data } = await apiClient.put<ApiResponse<PointsConfiguration> | PointsConfiguration>(`${BASE}/${id}`, toWirePayload(payload));
    return unwrap<PointsConfiguration>(data);
  },

  submit: async (id: string): Promise<PointsConfiguration> => {
    const { data } = await apiClient.post<ApiResponse<PointsConfiguration> | PointsConfiguration>(`${BASE}/${id}/submit`, {});
    return unwrap<PointsConfiguration>(data);
  },

  approve: async (id: string): Promise<PointsConfiguration> => {
    const { data } = await apiClient.post<ApiResponse<PointsConfiguration> | PointsConfiguration>(`${BASE}/${id}/approve`, {});
    return unwrap<PointsConfiguration>(data);
  },

  deactivate: async (id: string): Promise<PointsConfiguration> => {
    const { data } = await apiClient.post<ApiResponse<PointsConfiguration> | PointsConfiguration>(`${BASE}/${id}/deactivate`, {});
    return unwrap<PointsConfiguration>(data);
  },

  reject: async (id: string, rejectionReason: string): Promise<PointsConfiguration> => {
    const { data } = await apiClient.post<ApiResponse<PointsConfiguration> | PointsConfiguration>(
      `${BASE}/${id}/reject`,
      { rejectionReason }
    );
    return unwrap<PointsConfiguration>(data);
  },
};
